import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { gekkoApi } from '../../api/GekkoApi';
import { PaginatedResponse, Symptom } from '../../types/api.types';

// Query parameters interface for symptoms API
export interface SymptomsQueryParams {
  page?: number;
  page_size?: number;
  top_level_only?: boolean;
  parent_symptom?: number;
  leaves_only?: boolean;
  ids?: string; // Comma-separated list of IDs to filter by
  q?: string; // Search query for filtering symptoms by text
}

// Context interface
export interface SymptomsContextType {
  symptoms: Symptom[];
  isLoading: boolean;
  error: string | null;
  fetchSymptoms: (params?: SymptomsQueryParams) => Promise<Symptom[]>;
  refreshSymptoms: () => Promise<void>;
  setSymptoms: (symptoms: Symptom[]) => void;
  ensureSymptoms: (symptomIds: number[]) => Promise<void>;
}

// Create the context with a default value
const SymptomsContext = createContext<SymptomsContextType | undefined>(
  undefined
);

// Create the provider component
interface SymptomsProviderProps {
  children: ReactNode;
}

export const SymptomsProvider: React.FC<SymptomsProviderProps> = ({
  children,
}) => {
  const [symptoms, setSymptoms] = useState<Symptom[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastQueryParams, setLastQueryParams] = useState<SymptomsQueryParams>(
    {}
  );
  // Track symptom IDs that couldn't be fetched to prevent infinite loops
  const [unfetchableIds, setUnfetchableIds] = useState<Set<number>>(new Set());
  // Track fetch attempts per symptom ID to detect repeated failures
  const [fetchAttempts, setFetchAttempts] = useState<Map<number, number>>(
    new Map()
  );
  const MAX_FETCH_ATTEMPTS = 2;

  // Function to fetch symptoms from the API
  const fetchSymptoms = useCallback(
    async (params: SymptomsQueryParams = {}) => {
      setIsLoading(true);
      setError(null);

      try {
        let allSymptoms: Symptom[] = [];
        let currentPage = 1;
        let hasMorePages = true;
        const pageSize = params.page_size || 100;

        // If specific page is requested, only fetch that page
        if (params.page) {
          currentPage = params.page;
          hasMorePages = false;
        }

        // Fetch all pages until there are no more items (unless specific page requested)
        while (hasMorePages) {
          const queryParams = {
            ...params,
            page_size: pageSize,
            page: currentPage,
          };

          const response = await gekkoApi.get<PaginatedResponse<Symptom>>(
            '/symptoms',
            {
              queryParams,
            }
          );

          if (response.items && response.items.length > 0) {
            allSymptoms = [...allSymptoms, ...response.items];

            // Check if we've received fewer items than the page size or exactly the page size but total items reached
            hasMorePages =
              response.items.length === pageSize &&
              (response.total_items
                ? allSymptoms.length < response.total_items
                : true) &&
              !params.page; // Don't fetch more pages if specific page was requested

            currentPage++;
          } else {
            // No more items
            hasMorePages = false;
          }
        }

        // Update state with all fetched symptoms
        setSymptoms(allSymptoms);
        setLastQueryParams(params);

        return allSymptoms;
      } catch (err) {
        console.error('Error fetching symptoms:', err);
        setError('Failed to fetch symptoms. Please try again later.');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Function to refresh symptoms using the last query parameters
  const refreshSymptoms = useCallback(async () => {
    return fetchSymptoms(lastQueryParams).then(() => {});
  }, [fetchSymptoms, lastQueryParams]);

  // Function to ensure specific symptoms are loaded
  const ensureSymptoms = useCallback(
    async (symptomIds: number[]) => {
      if (symptomIds.length === 0) return;

      // Find missing symptom IDs (excluding already known unfetchable ones)
      const existingSymptomIds = new Set(symptoms.map((symptom) => symptom.id));
      const missingIds = symptomIds.filter(
        (id) => !existingSymptomIds.has(id) && !unfetchableIds.has(id)
      );

      if (missingIds.length === 0) return; // All symptoms already loaded or marked unfetchable

      // Check fetch attempts and filter out IDs that have exceeded max attempts
      const idsToFetch = missingIds.filter((id) => {
        const attempts = fetchAttempts.get(id) || 0;
        return attempts < MAX_FETCH_ATTEMPTS;
      });

      if (idsToFetch.length === 0) return; // All remaining IDs have exceeded max attempts

      // Update fetch attempts
      setFetchAttempts((prev) => {
        const newAttempts = new Map(prev);
        idsToFetch.forEach((id) => {
          newAttempts.set(id, (newAttempts.get(id) || 0) + 1);
        });
        return newAttempts;
      });

      try {
        // Fetch missing symptoms by IDs
        const fetchedSymptoms = await fetchSymptoms({
          ids: idsToFetch.join(','),
        });

        // Find IDs that were requested but not returned (don't exist in DB)
        const fetchedIds = new Set(fetchedSymptoms.map((s) => s.id));
        const notFoundIds = idsToFetch.filter((id) => !fetchedIds.has(id));

        // Check if not found IDs have reached max attempts
        const newUnfetchableIds: number[] = [];
        notFoundIds.forEach((id) => {
          const attempts = (fetchAttempts.get(id) || 0) + 1; // +1 because we just attempted
          if (attempts >= MAX_FETCH_ATTEMPTS) {
            newUnfetchableIds.push(id);
          }
        });

        // Create placeholder symptoms for unfetchable IDs
        const placeholderSymptoms: Symptom[] = newUnfetchableIds.map((id) => ({
          id,
          name: `Unknown symptom (id: ${id})`,
          symptom_type: {
            id: 0,
            name: 'Unknown',
            category: {
              id: 0,
              name: 'Unknown',
            },
          },
        }));

        // Mark these IDs as unfetchable
        if (newUnfetchableIds.length > 0) {
          setUnfetchableIds((prev) => {
            const newSet = new Set(prev);
            newUnfetchableIds.forEach((id) => newSet.add(id));
            return newSet;
          });

          // Clear fetch attempts for unfetchable IDs (no longer needed)
          setFetchAttempts((prev) => {
            const newAttempts = new Map(prev);
            newUnfetchableIds.forEach((id) => newAttempts.delete(id));
            return newAttempts;
          });
        }

        // Append fetched symptoms and placeholders to existing ones (avoid duplicates)
        setSymptoms((prevSymptoms) => {
          const existingIds = new Set(prevSymptoms.map((s) => s.id));
          const newSymptoms = [
            ...fetchedSymptoms.filter((s) => !existingIds.has(s.id)),
            ...placeholderSymptoms.filter((s) => !existingIds.has(s.id)),
          ];
          return [...prevSymptoms, ...newSymptoms];
        });
      } catch (err) {
        console.error('Error ensuring symptoms:', err);
        // Don't throw error to avoid breaking the UI
      }
    },
    [symptoms, fetchSymptoms, unfetchableIds, fetchAttempts]
  );

  // Context value
  const value: SymptomsContextType = {
    symptoms,
    isLoading,
    error,
    fetchSymptoms,
    refreshSymptoms,
    setSymptoms,
    ensureSymptoms,
  };

  return (
    <SymptomsContext.Provider value={value}>
      {children}
    </SymptomsContext.Provider>
  );
};

// Custom hook to use the symptoms context
export const useSymptoms = () => {
  const context = useContext(SymptomsContext);
  if (context === undefined) {
    throw new Error('useSymptoms must be used within a SymptomsProvider');
  }
  return context;
};
